import {
  buildOrganizationEventId,
  isOrganizationEventStoragePath,
  isOrganizationEventStatus,
  isOrganizationEventType,
  normalizeOrganizationEventId,
  type OrganizationEventStatus,
  type OrganizationEventType,
} from "../../../packages/foundation/src/index.js";

export type EventEditorInput = {
  eventId: string;
  status: OrganizationEventStatus;
  eventType: OrganizationEventType;
  scheduledPublishDate: string;
  title: string;
  titleAr: string;
  summary: string;
  summaryAr: string;
  description: string;
  descriptionAr: string;
  imageStoragePath: string;
  imageAltText: string;
  imageAltTextAr: string;
  locationName: string;
  addressLine1: string;
  locationType: "physical" | "virtual" | "hybrid";
  onlineUrl: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  campusId: string;
  campusNameSnapshot: string;
  registrationMode: "none" | "free";
  registrationEnabled: boolean;
  registrationOpensAt: string;
  registrationClosesAt: string;
  registrationCapacity: string;
  registrationCancellationAllowed: boolean;
  guestRegistrationEnabled: boolean;
  dependentRegistrationEnabled: boolean;
  waitlistEnabled: boolean;
  registrationQuestionLabel: string;
  registrationQuestionRequired: boolean;
  waiverRequired: boolean;
  waiverTitle: string;
  waiverBody: string;
  reminderOneDayBefore: boolean;
  reminderTwoHoursBefore: boolean;
  notificationRequestedOnPublish: boolean;
  emailRequestedOnPublish?: boolean;
};

export type EventEditorWizardStepId =
  "template" | "details" | "schedule" | "registration" | "media" | "communication" | "review";

export type EventCommunicationDeliveryType = "eventPublished" | "materialUpdate" | "urgentNotice";

export const eventEditorWizardStepIds: EventEditorWizardStepId[] = [
  "template",
  "details",
  "schedule",
  "registration",
  "media",
  "communication",
  "review",
];

export function eventTypeUsesRegistrationWizardStep(eventType: OrganizationEventType): boolean {
  return eventType === "standard";
}

export function eventWizardStepIdsForEventType(
  eventType: OrganizationEventType,
): EventEditorWizardStepId[] {
  return eventTypeUsesRegistrationWizardStep(eventType)
    ? eventEditorWizardStepIds
    : eventEditorWizardStepIds.filter((stepId) => stepId !== "registration");
}

export function defaultEventEmailDeliveryTypeForEventType(
  eventType: OrganizationEventType,
): EventCommunicationDeliveryType {
  return eventType === "janazah" ? "urgentNotice" : "eventPublished";
}

export type EventEditorDraft = {
  emailRequestedOnPublish: boolean;
  eventId: string;
  status: OrganizationEventStatus;
  eventType: OrganizationEventType;
  scheduledPublishAt: Date | null;
  title: string;
  titleAr: string | null;
  summary: string;
  summaryAr: string | null;
  description: string;
  descriptionAr: string | null;
  image: {
    storagePath: string | null;
    altText: string | null;
    altTextAr: string | null;
  };
  locationName: string;
  addressLine1: string | null;
  locationType: "physical" | "virtual" | "hybrid";
  onlineUrl: string | null;
  startsAt: Date;
  endsAt: Date;
  allDay: false;
  timezone: string;
  campusId: string | null;
  campusNameSnapshot: string | null;
  registrationMode: "none" | "free";
  registrationEnabled: boolean;
  registrationOpensAt: Date | null;
  registrationClosesAt: Date | null;
  registrationCapacity: number;
  registrationConfirmedCount: number;
  registrationCancellationAllowed: boolean;
  guestRegistrationEnabled: boolean;
  dependentRegistrationEnabled: boolean;
  waitlistEnabled: boolean;
  waitlistCount: number;
  registrationQuestionCount: number;
  registrationQuestions: Array<{
    questionId: string;
    type: "short_text";
    target: "registration";
    label: string;
    labelAr: null;
    helpText: null;
    helpTextAr: null;
    required: boolean;
    options: [];
    position: number;
  }>;
  waiverRequired: boolean;
  waiver: {
    waiverId: string;
    version: number;
    title: string;
    titleAr: null;
    body: string;
    bodyAr: null;
    hash: string;
    publishedAt: Date;
    updatedAt: Date;
    updatedBy: string;
  } | null;
  reminderPresets: string[];
  attendanceCheckedInCount: number;
  notificationRequestedOnPublish: boolean;
  visibility: "connected";
  source: "p14_events";
};

export type EventEditorResult =
  | {
      ok: true;
      draft: EventEditorDraft;
    }
  | {
      ok: false;
      errors: string[];
    };

export type EventEditorFixture = {
  allDay?: boolean;
  campusId?: string | null;
  campusNameSnapshot?: string | null;
  description?: string;
  descriptionAr?: string | null;
  endsAt: string;
  eventId?: string;
  id: string;
  image?: {
    storagePath: string | null;
    altText: string | null;
    altTextAr: string | null;
  };
  locationName: string;
  addressLine1?: string | null;
  locationType?: "physical" | "virtual" | "hybrid";
  onlineUrl?: string | null;
  notificationRequestedOnPublish?: boolean;
  emailRequestedOnPublish?: boolean;
  registrationMode?: "none" | "free";
  registrationEnabled?: boolean;
  registrationOpensAt?: string | null;
  registrationClosesAt?: string | null;
  registrationCapacity?: number;
  registrationConfirmedCount?: number;
  registrationCancellationAllowed?: boolean;
  guestRegistrationEnabled?: boolean;
  dependentRegistrationEnabled?: boolean;
  waitlistEnabled?: boolean;
  waitlistCount?: number;
  attendanceCheckedInCount?: number;
  startsAt: string;
  status?: OrganizationEventStatus;
  eventType?: OrganizationEventType;
  scheduledPublishAt?: string | null;
  summary: string;
  summaryAr: string | null;
  timezone?: string;
  title: string;
  titleAr: string | null;
};

const titleLimit = 140;
const summaryLimit = 300;
const descriptionLimit = 3000;
const locationLimit = 180;
const timezoneLimit = 80;

export function buildEventEditorInputFromFixture(fixture: EventEditorFixture): EventEditorInput {
  return {
    campusId: fixture.campusId ?? "",
    campusNameSnapshot: fixture.campusNameSnapshot ?? "",
    description: fixture.description ?? fixture.summary,
    descriptionAr: fixture.descriptionAr ?? fixture.summaryAr ?? "",
    endsAt: formatDateTimeLocalValue(new Date(fixture.endsAt)),
    eventId: fixture.eventId ?? fixture.id,
    eventType: fixture.eventType ?? "standard",
    imageAltText: fixture.image?.altText ?? "",
    imageAltTextAr: fixture.image?.altTextAr ?? "",
    imageStoragePath: fixture.image?.storagePath ?? "",
    addressLine1: fixture.addressLine1 ?? "",
    locationType: fixture.locationType ?? "physical",
    locationName: fixture.locationName,
    onlineUrl: fixture.onlineUrl ?? "",
    notificationRequestedOnPublish: fixture.notificationRequestedOnPublish ?? false,
    emailRequestedOnPublish: fixture.emailRequestedOnPublish === true,
    registrationMode:
      fixture.registrationMode ?? (fixture.registrationEnabled === false ? "none" : "free"),
    registrationCancellationAllowed: fixture.registrationCancellationAllowed ?? true,
    guestRegistrationEnabled: fixture.guestRegistrationEnabled ?? true,
    dependentRegistrationEnabled: fixture.dependentRegistrationEnabled ?? true,
    registrationCapacity: String(fixture.registrationCapacity ?? 0),
    registrationClosesAt: fixture.registrationClosesAt
      ? formatDateTimeLocalValue(new Date(fixture.registrationClosesAt))
      : "",
    registrationEnabled: fixture.registrationEnabled ?? false,
    registrationOpensAt: fixture.registrationOpensAt
      ? formatDateTimeLocalValue(new Date(fixture.registrationOpensAt))
      : "",
    waitlistEnabled: fixture.waitlistEnabled ?? false,
    registrationQuestionLabel: "",
    registrationQuestionRequired: false,
    waiverRequired: false,
    waiverTitle: "",
    waiverBody: "",
    reminderOneDayBefore: true,
    reminderTwoHoursBefore: true,
    scheduledPublishDate: fixture.scheduledPublishAt
      ? formatDateForTimeZone(
          new Date(fixture.scheduledPublishAt),
          fixture.timezone ?? "America/New_York",
        )
      : "",
    startsAt: formatDateTimeLocalValue(new Date(fixture.startsAt)),
    status: fixture.status ?? "published",
    summary: fixture.summary,
    summaryAr: fixture.summaryAr ?? "",
    timezone: fixture.timezone ?? "America/New_York",
    title: fixture.title,
    titleAr: fixture.titleAr ?? "",
  };
}

export function buildDefaultEventEditorInput(organizationId: string): EventEditorInput {
  const startsAt = new Date("2026-10-17T22:00:00.000Z");
  const eventId = buildOrganizationEventId({
    organizationId,
    startsAt,
    suffix: "admin",
    title: "Community gathering",
  });

  return {
    campusId: "",
    campusNameSnapshot: "",
    description:
      "A community gathering for the connected organization with details, location, and schedule managed by the organization team.",
    descriptionAr: "لقاء مجتمعي للمؤسسة المتصلة تدير فيه جهة المؤسسة التفاصيل والموقع والجدول.",
    endsAt: "2026-10-17T20:00",
    eventId,
    eventType: "standard",
    imageAltText: "",
    imageAltTextAr: "",
    imageStoragePath: "",
    addressLine1: "",
    locationType: "physical",
    locationName: "Main hall",
    onlineUrl: "",
    notificationRequestedOnPublish: false,
    registrationMode: "free",
    registrationCancellationAllowed: true,
    guestRegistrationEnabled: true,
    dependentRegistrationEnabled: true,
    registrationCapacity: "50",
    registrationClosesAt: "2026-10-17T17:00",
    registrationEnabled: true,
    registrationOpensAt: "2026-08-08T12:00",
    waitlistEnabled: true,
    registrationQuestionLabel: "",
    registrationQuestionRequired: false,
    waiverRequired: false,
    waiverTitle: "",
    waiverBody: "",
    reminderOneDayBefore: true,
    reminderTwoHoursBefore: true,
    scheduledPublishDate: "",
    startsAt: "2026-10-17T18:00",
    status: "published",
    summary: "A community gathering for the connected organization.",
    summaryAr: "لقاء مجتمعي للمؤسسة المتصلة.",
    timezone: "America/New_York",
    title: "Community gathering",
    titleAr: "لقاء مجتمعي",
  };
}

export function evaluateEventEditorInput(
  input: EventEditorInput,
  organizationId: string,
): EventEditorResult {
  const eventId = normalizeEventDocumentId(input.eventId);
  const title = input.title.trim();
  const titleAr = nullableText(input.titleAr);
  const summary = input.summary.trim();
  const summaryAr = nullableText(input.summaryAr);
  const description = input.description.trim();
  const descriptionAr = nullableText(input.descriptionAr);
  const locationName = input.locationName.trim();
  const startsAt = parseDateTimeLocal(input.startsAt);
  const endsAt = parseDateTimeLocal(input.endsAt);
  const timezone = input.timezone.trim();
  const campusId = nullableText(input.campusId);
  const campusNameSnapshot = nullableText(input.campusNameSnapshot);
  const imageStoragePath = nullableText(input.imageStoragePath);
  const imageAltText = nullableText(input.imageAltText);
  const imageAltTextAr = nullableText(input.imageAltTextAr);
  const addressLine1 = nullableText(input.addressLine1);
  const onlineUrl = nullableText(input.onlineUrl);
  const registrationOpensAt = parseOptionalDateTimeLocal(input.registrationOpensAt);
  const registrationClosesAt = parseOptionalDateTimeLocal(input.registrationClosesAt);
  const registrationCapacity =
    !input.registrationEnabled && input.registrationCapacity.trim().length === 0
      ? 0
      : parseInteger(input.registrationCapacity);
  const registrationQuestionLabel = nullableText(input.registrationQuestionLabel);
  const waiverTitle = nullableText(input.waiverTitle);
  const waiverBody = nullableText(input.waiverBody);
  const scheduledPublishAt =
    input.status === "scheduled"
      ? parseLocalDateAtStartOfDay(input.scheduledPublishDate, timezone)
      : null;
  const errors: string[] = [];

  validateRequiredText("Event id", eventId, 100, errors);
  validateRequiredText("English title", title, titleLimit, errors);
  validateOptionalText("Arabic title", titleAr, titleLimit, errors);
  validateRequiredText("English summary", summary, summaryLimit, errors);
  validateOptionalText("Arabic summary", summaryAr, summaryLimit, errors);
  validateRequiredText("English description", description, descriptionLimit, errors);
  validateOptionalText("Arabic description", descriptionAr, descriptionLimit, errors);
  validateRequiredText("Location", locationName, locationLimit, errors);
  validateRequiredText("Timezone", timezone, timezoneLimit, errors);
  validateOptionalText("Address", addressLine1, 240, errors);
  validateOptionalText("Online URL", onlineUrl, 1000, errors);

  if (timezone && !isSupportedTimeZone(timezone)) {
    errors.push("Timezone must be selected from the supported timezone list.");
  }

  if (!isOrganizationEventStatus(input.status)) {
    errors.push("Event status must be a supported event lifecycle state.");
  }

  if (!isOrganizationEventType(input.eventType)) {
    errors.push("Event type must be standard, Friday program, or Janazah.");
  }

  if (input.registrationMode !== "none" && input.registrationMode !== "free") {
    errors.push("Registration mode must be none or free in P14.");
  }

  if (
    input.locationType !== "physical" &&
    input.locationType !== "virtual" &&
    input.locationType !== "hybrid"
  ) {
    errors.push("Location type must be physical, virtual, or hybrid.");
  }

  if (input.status === "scheduled" && scheduledPublishAt === undefined) {
    errors.push("Scheduled publish date is required.");
  }

  if (!startsAt) {
    errors.push("Start date and time is required.");
  }

  if (!endsAt) {
    errors.push("End date and time is required.");
  }

  if (startsAt && endsAt && startsAt >= endsAt) {
    errors.push("End date and time must be after the start date and time.");
  }

  if (input.registrationOpensAt.trim() && !registrationOpensAt) {
    errors.push("Registration open time must be a valid date and time.");
  }

  if (input.registrationClosesAt.trim() && !registrationClosesAt) {
    errors.push("Registration close time must be a valid date and time.");
  }

  if (registrationOpensAt && registrationClosesAt && registrationOpensAt >= registrationClosesAt) {
    errors.push("Registration close time must be after the open time.");
  }

  if (
    registrationCapacity === undefined ||
    registrationCapacity < 0 ||
    registrationCapacity > 100000 ||
    (input.registrationEnabled && registrationCapacity === 0)
  ) {
    errors.push("Registration capacity must be valid for the registration state.");
  }

  if (registrationQuestionLabel) {
    validateRequiredText("Registration question", registrationQuestionLabel, 180, errors);
  }

  if (input.waiverRequired) {
    validateRequiredText("Waiver title", waiverTitle ?? "", 180, errors);
    validateRequiredText("Waiver body", waiverBody ?? "", 12000, errors);
  }

  if (campusId && !campusNameSnapshot) {
    errors.push("Campus name snapshot is required when a campus is selected.");
  }

  if (!campusId && campusNameSnapshot) {
    errors.push("Campus name snapshot must be blank when no campus is selected.");
  }

  if (
    imageStoragePath &&
    !isOrganizationEventStoragePath(organizationId, eventId, imageStoragePath)
  ) {
    errors.push("Image storage path must stay under the organization event path.");
  }

  if (
    errors.length > 0 ||
    !startsAt ||
    !endsAt ||
    registrationOpensAt === undefined ||
    registrationClosesAt === undefined ||
    registrationCapacity === undefined ||
    scheduledPublishAt === undefined ||
    !isOrganizationEventStatus(input.status) ||
    !isOrganizationEventType(input.eventType)
  ) {
    return { ok: false, errors };
  }

  return {
    ok: true,
    draft: {
      allDay: false,
      campusId,
      campusNameSnapshot,
      description,
      descriptionAr,
      endsAt,
      eventId,
      eventType: input.eventType,
      image: {
        altText: imageAltText,
        altTextAr: imageAltTextAr,
        storagePath: imageStoragePath,
      },
      addressLine1,
      locationType: input.locationType,
      locationName,
      onlineUrl,
      notificationRequestedOnPublish: input.notificationRequestedOnPublish,
      emailRequestedOnPublish: input.emailRequestedOnPublish === true,
      registrationMode: input.registrationEnabled ? input.registrationMode : "none",
      registrationCancellationAllowed: input.registrationCancellationAllowed,
      guestRegistrationEnabled: input.registrationEnabled && input.guestRegistrationEnabled,
      dependentRegistrationEnabled: input.registrationEnabled && input.dependentRegistrationEnabled,
      registrationCapacity,
      registrationClosesAt,
      registrationConfirmedCount: 0,
      registrationEnabled: input.registrationEnabled,
      registrationOpensAt,
      waitlistCount: 0,
      waitlistEnabled: input.registrationEnabled && input.waitlistEnabled,
      registrationQuestionCount: registrationQuestionLabel ? 1 : 0,
      registrationQuestions: registrationQuestionLabel
        ? [
            {
              helpText: null,
              helpTextAr: null,
              label: registrationQuestionLabel,
              labelAr: null,
              options: [],
              position: 0,
              questionId: "event-question-1",
              required: input.registrationQuestionRequired,
              target: "registration",
              type: "short_text",
            },
          ]
        : [],
      waiverRequired: input.waiverRequired,
      waiver:
        input.waiverRequired && waiverTitle && waiverBody
          ? {
              body: waiverBody,
              bodyAr: null,
              hash: waiverHash(waiverTitle, waiverBody),
              publishedAt: new Date(),
              title: waiverTitle,
              titleAr: null,
              updatedAt: new Date(),
              updatedBy: "org-admin",
              version: 1,
              waiverId: "event-waiver",
            }
          : null,
      reminderPresets: [
        ...(input.reminderOneDayBefore ? ["one_day_before"] : []),
        ...(input.reminderTwoHoursBefore ? ["two_hours_before"] : []),
      ],
      attendanceCheckedInCount: 0,
      scheduledPublishAt,
      source: "p14_events",
      startsAt,
      status: input.status,
      summary,
      summaryAr,
      timezone,
      title,
      titleAr,
      visibility: "connected",
    },
  };
}

export function normalizeEventDocumentId(value: string): string {
  return normalizeOrganizationEventId(value);
}

export function fridayProgramDefaultRange(from = new Date()): { endsAt: Date; startsAt: Date } {
  const startsAt = nextWeekdayAt(5, 13, 0, from);
  return {
    endsAt: dateWithTime(startsAt, 22, 0),
    startsAt,
  };
}

export function formatDateTimeLocalValue(date: Date): string {
  const year = date.getFullYear();
  const month = padDatePart(date.getMonth() + 1);
  const day = padDatePart(date.getDate());
  const hours = padDatePart(date.getHours());
  const minutes = padDatePart(date.getMinutes());

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function nextWeekdayAt(weekday: number, hour: number, minute: number, from: Date): Date {
  const date = new Date(from);
  const delta = (weekday - date.getDay() + 7) % 7 || 7;
  date.setDate(date.getDate() + delta);
  date.setHours(hour, minute, 0, 0);
  return date;
}

function dateWithTime(date: Date, hour: number, minute: number): Date {
  const result = new Date(date);
  result.setHours(hour, minute, 0, 0);
  return result;
}

function parseDateTimeLocal(value: string): Date | undefined {
  if (!value.trim()) {
    return undefined;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

function parseOptionalDateTimeLocal(value: string): Date | null | undefined {
  if (!value.trim()) {
    return null;
  }

  return parseDateTimeLocal(value);
}

function parseLocalDateAtStartOfDay(value: string, timezone: string): Date | undefined {
  if (!value.trim()) {
    return undefined;
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value.trim());
  if (!match?.[1] || !match[2] || !match[3] || !isSupportedTimeZone(timezone)) {
    return undefined;
  }

  const yearValue = match[1];
  const monthValue = match[2];
  const dayValue = match[3];
  const target = {
    day: Number.parseInt(dayValue, 10),
    hour: 0,
    minute: 0,
    month: Number.parseInt(monthValue, 10),
    year: Number.parseInt(yearValue, 10),
  };
  if (
    !Number.isSafeInteger(target.year) ||
    !Number.isSafeInteger(target.month) ||
    !Number.isSafeInteger(target.day) ||
    target.month < 1 ||
    target.month > 12 ||
    target.day < 1 ||
    target.day > 31
  ) {
    return undefined;
  }

  let instant = Date.UTC(target.year, target.month - 1, target.day, 0, 0, 0, 0);
  const targetUtc = instant;
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const parts = zonedDateTimeParts(new Date(instant), timezone);
    const representedUtc = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      0,
      0,
    );
    const diff = representedUtc - targetUtc;
    if (diff === 0) {
      return new Date(instant);
    }
    instant -= diff;
  }

  const candidate = new Date(instant);
  const parts = zonedDateTimeParts(candidate, timezone);
  return parts.year === target.year &&
    parts.month === target.month &&
    parts.day === target.day &&
    parts.hour === 0 &&
    parts.minute === 0
    ? candidate
    : undefined;
}

function formatDateForTimeZone(date: Date, timezone: string): string {
  if (!isSupportedTimeZone(timezone) || Number.isNaN(date.getTime())) {
    return "";
  }

  const parts = zonedDateTimeParts(date, timezone);
  return `${parts.year}-${padDatePart(parts.month)}-${padDatePart(parts.day)}`;
}

function zonedDateTimeParts(
  date: Date,
  timezone: string,
): {
  day: number;
  hour: number;
  minute: number;
  month: number;
  year: number;
} {
  const parts = new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    hour: "2-digit",
    hour12: false,
    hourCycle: "h23",
    minute: "2-digit",
    month: "2-digit",
    timeZone: timezone,
    year: "numeric",
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes): number =>
    Number.parseInt(parts.find((part) => part.type === type)?.value ?? "", 10);
  return {
    day: value("day"),
    hour: value("hour"),
    minute: value("minute"),
    month: value("month"),
    year: value("year"),
  };
}

function isSupportedTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

function parseInteger(value: string): number | undefined {
  if (!/^\d+$/.test(value.trim())) {
    return undefined;
  }

  const parsed = Number.parseInt(value.trim(), 10);
  return Number.isSafeInteger(parsed) ? parsed : undefined;
}

function waiverHash(title: string, body: string): string {
  let hash = 0x811c9dc5;
  for (const codeUnit of `${title}\n${body}`) {
    hash ^= codeUnit.codePointAt(0) ?? 0;
    hash = Math.imul(hash, 0x01000193);
  }
  return `fnv1a:${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

function nullableText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function validateRequiredText(
  label: string,
  value: string,
  maxLength: number,
  errors: string[],
): void {
  if (!value) {
    errors.push(`${label} is required.`);
    return;
  }

  if (value.length > maxLength) {
    errors.push(`${label} must be ${maxLength} characters or fewer.`);
  }
}

function validateOptionalText(
  label: string,
  value: string | null,
  maxLength: number,
  errors: string[],
): void {
  if (value === null) {
    return;
  }

  if (value.length > maxLength) {
    errors.push(`${label} must be ${maxLength} characters or fewer.`);
  }
}

function padDatePart(value: number): string {
  return String(value).padStart(2, "0");
}
